import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Apple, Check, Eye, EyeOff, Lock, MapPinned, User } from 'lucide-react';
import { authService } from '../services/api';
import { useAuthStore } from '../store';
import { toast } from 'react-toastify';

const heroImageUrl = '/images/login-neon-space-satellites.png';
const androidAppUrl = process.env.REACT_APP_ANDROID_APP_URL || 'https://www.rcrastro.com.br/downloads/rcrastro.apk';
const iosAppUrl = process.env.REACT_APP_IOS_APP_URL || 'https://www.rcrastro.com.br';
const showAppLinks = process.env.REACT_APP_SHOW_APP_LINKS !== 'false';
const visitorCredentials = {
  email: process.env.REACT_APP_VISITOR_EMAIL || 'visitante@rcrastro.com',
  password: process.env.REACT_APP_VISITOR_PASSWORD || 'visitante123',
};

const Login = () => {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const response = await authService.login(formData);
      const { user, token } = response.data.data;

      setAuth(user, token);
      toast.success(`Bem-vindo, ${user.name}!`);
      navigate('/tracking');
    } catch (error) {
      console.error('Erro no login:', error);
      toast.error(error.response?.data?.message || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleVisitorLogin = async () => {
    setLoading(true);

    try {
      const response = await authService.login(visitorCredentials);
      const { user, token } = response.data.data;

      setAuth(user, token);
      toast.info('Acesso visitante ativado em modo somente leitura');
      navigate('/tracking');
    } catch (error) {
      console.error('Erro no login visitante:', error);
      toast.error(error.response?.data?.message || 'Erro ao entrar como visitante');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#050914] bg-cover bg-center px-4 py-4 text-white"
      style={{ backgroundImage: `url(${heroImageUrl})` }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-[#061528]/62 to-black/88" />
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(0,229,255,0.22),transparent_36%,rgba(0,119,255,0.18))]" />
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-[radial-gradient(circle_at_50%_100%,rgba(0,183,255,0.22),transparent_65%)]" />

      <section className="relative w-full max-w-[410px] rounded-lg border border-primary-300/35 bg-[#06101d]/92 p-5 shadow-2xl shadow-primary-500/20 backdrop-blur-md sm:p-7">
        <div className="mb-6 flex justify-center">
          <div className="flex items-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-full border-[5px] border-primary-300 text-primary-200 shadow-lg shadow-primary-400/35">
              <MapPinned className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-3xl font-black italic tracking-tight text-primary-200 drop-shadow-[0_0_14px_rgba(0,183,255,0.62)]">RCrastro</h1>
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-cyan-100/70">
                Rastreamento veicular
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-2.5">
          <label className="relative block">
            <User className="absolute left-6 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9a9a9a]" />
            <input
              type="text"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="h-12 w-full rounded border border-primary-300/20 bg-[#030a14]/90 pl-16 pr-4 text-sm text-white outline-none transition placeholder:text-cyan-100/45 focus:border-primary-300 focus:ring-2 focus:ring-primary-300/20"
              placeholder="Usuario"
              required
            />
          </label>

          <label className="relative block">
            <Lock className="absolute left-6 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9a9a9a]" />
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="h-12 w-full rounded border border-primary-300/20 bg-[#030a14]/90 pl-16 pr-12 text-sm text-white outline-none transition placeholder:text-cyan-100/45 focus:border-primary-300 focus:ring-2 focus:ring-primary-300/20"
              placeholder="Senha"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-5 top-1/2 -translate-y-1/2 text-[#999] transition-colors hover:text-[#555]"
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </label>

          <label className="flex items-center gap-3 py-1 text-sm text-cyan-100/70">
            <span className="relative flex h-5 w-5 items-center justify-center border border-primary-300/60 bg-[#030a14]">
              <input
                type="checkbox"
                checked={remember}
                onChange={(event) => setRemember(event.target.checked)}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              />
              {remember && <Check className="h-4 w-4 text-primary-200" />}
            </span>
            Lembre-me
          </label>

          <button
            type="submit"
            disabled={loading}
            className="mt-4 flex h-12 w-full items-center justify-center rounded-lg bg-gradient-to-r from-primary-400 to-blue-600 px-4 text-sm font-black uppercase tracking-[0.12em] text-white shadow-lg shadow-primary-400/30 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </button>

          <button
            type="button"
            onClick={handleVisitorLogin}
            disabled={loading}
            className="flex h-11 w-full items-center justify-center rounded-lg border border-primary-300/35 bg-[#031426]/80 px-4 text-xs font-black uppercase tracking-[0.12em] text-primary-100 shadow-lg shadow-primary-400/10 transition hover:border-primary-200 hover:bg-[#06213b] disabled:cursor-not-allowed disabled:opacity-70"
          >
            Entrar como visitante
          </button>
        </form>

        {showAppLinks && (
          <div className="mt-4 grid grid-cols-1 gap-2.5 border-t border-cyan-400/15 pt-4 sm:grid-cols-2">
            <a
              href={androidAppUrl}
              target="_blank"
              rel="noreferrer"
              className="flex h-11 min-w-0 items-center justify-center gap-2 rounded-lg border border-white/15 bg-black px-3 text-left text-white shadow-md shadow-primary-400/20 transition-transform hover:-translate-y-0.5"
              aria-label="Baixar app Android"
              title="Baixar app Android"
            >
              <svg className="h-5 w-5 shrink-0" viewBox="0 0 32 32" aria-hidden="true">
                <path fill="#00f0ff" d="M4 3.8v24.4l13.2-12.2z" />
                <path fill="#31f16b" d="M4 3.8 21 13.5l-3.8 2.5z" />
                <path fill="#ffe15a" d="m17.2 16 3.8 2.5L4 28.2z" />
                <path fill="#ff4b55" d="m21 13.5 6 3.5-6 1.5-3.8-2.5z" />
              </svg>
              <span className="min-w-0 leading-none">
                <span className="block text-[9px] uppercase tracking-wide text-white/70">Baixar no</span>
                <span className="block truncate text-sm font-bold">Google Play</span>
              </span>
            </a>

            <a
              href={iosAppUrl}
              target="_blank"
              rel="noreferrer"
              className="flex h-11 min-w-0 items-center justify-center gap-2 rounded-lg border border-white/15 bg-black px-3 text-left text-white shadow-md shadow-cyan-500/20 transition-transform hover:-translate-y-0.5"
              aria-label="Abrir no iPhone"
              title="Abrir no iPhone"
            >
              <Apple className="h-5 w-5 shrink-0" />
              <span className="min-w-0 leading-none">
                <span className="block text-[9px] uppercase tracking-wide text-white/70">Baixar na</span>
                <span className="block truncate text-sm font-bold">App Store</span>
              </span>
            </a>
          </div>
        )}

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => toast.info('Recuperacao de senha em breve')}
            className="text-xs font-medium text-cyan-100/70 transition hover:text-primary-200"
          >
            Recuperar a senha
          </button>
          <p className="mt-3 text-xs text-cyan-100/55">Nova versao do app disponivel. Atualize na loja do seu smartphone.</p>
        </div>
      </section>
    </main>
  );
};

export default Login;
